import React, { useEffect, useRef } from 'react'
import { Box, Paper, Typography, Divider } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import CancelIcon from '@mui/icons-material/Cancel'

// Mirrors the color/icon language of the global toast (src/@core/components/custom-Toast/showToast.js)
// so a save result reads the same whether it lands as a toast or inline.
const VARIANTS = {
  success: { icon: CheckCircleIcon, color: '#16a34a', label: 'Success' },
  error: { icon: ErrorIcon, color: '#dc2626', label: 'Error' }
}

const AUTO_HIDE_MS = 4000

const InlineSaveStatus = ({ type = 'error', message, onClose }) => {
  // onClose is typically a fresh arrow function on every parent render, so it's
  // read via a ref rather than a useEffect dependency — otherwise the timer would
  // keep restarting on unrelated parent re-renders and never actually auto-hide.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!message || type !== 'success') return
    const timer = setTimeout(() => onCloseRef.current?.(), AUTO_HIDE_MS)
    return () => clearTimeout(timer)
  }, [message, type])

  if (!message) return null
  const { icon: StatusIcon, color, label } = VARIANTS[type] || VARIANTS.error

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fff',
        borderRadius: 2,
        px: 2,
        py: 1,
        minWidth: 240,
        width: 'fit-content',
        maxWidth: '100%'
      }}
    >
      <StatusIcon sx={{ color, flexShrink: 0 }} />
      <Typography variant='subtitle2' sx={{ ml: 1, fontWeight: 600, color, flexShrink: 0 }}>
        {label}
      </Typography>
      <Divider orientation='vertical' flexItem sx={{ mx: 1 }} />
      <Typography variant='subtitle2' sx={{ color, mr: 1, whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {message}
      </Typography>
      {onClose && (
        <CancelIcon sx={{ cursor: 'pointer', color: 'grey', fontSize: '18px', flexShrink: 0 }} onClick={onClose} />
      )}
    </Paper>
  )
}

// Drop-in wrapper for a wizard's Back/Exit ... Save/Next button row:
// place <InlineSaveStatusSlot .../> between the left and right button Box's.
export const InlineSaveStatusSlot = props => (
  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: 2, minWidth: 0 }}>
    <InlineSaveStatus {...props} />
  </Box>
)

export default InlineSaveStatus
